import { InfoCircleOutlined } from '@ant-design/icons';
import { faFilePdf } from '@fortawesome/free-regular-svg-icons';
import { faCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Empty, Flex, Tooltip, Typography } from 'antd';
import { useCallback } from 'react';

import {
  getColumns,
  getTotals,
} from '@app/components/RecordModal/components/RecordPayment/data/constants';
import type {
  ServicesTableType,
  ServiceTableRow,
} from '@app/components/RecordModal/components/RecordPayment/types';
import '@app/components/RecordModal/components/RecordPayment/styles/ServicesTable.desktop.scss';
import type { ModelRecordServiceDiscount } from '@app/types';
import { divideAmount } from '@app/utils';

const { Text } = Typography;
const PF = 'services-table';

const BONUS_DISCOUNT_TYPES = ['Bonus', 'Bonuses'];

const getDiscountTooltipText = ({
  Discount,
  Type,
}: ModelRecordServiceDiscount) => {
  const discount = divideAmount(Discount ?? 0);

  if (Type === 'Discount') {
    return `${discount} - ручная скидка`;
  }

  if (BONUS_DISCOUNT_TYPES.includes(Type ?? '')) {
    return `${discount} - бонусы с карты лояльности`;
  }

  if (Type === 'LoyaltyDiscount') {
    return `${discount} - скидка по карте лояльности`;
  }

  return `${discount} - скидка`;
};

export const ServicesTable: ServicesTableType = ({
  data,
  isPrintPdfLoading,
  printPdfFile,
}) => {
  const columns = getColumns();
  const totals = getTotals();

  const getCell = useCallback(
    (key: keyof ServiceTableRow, row: ServiceTableRow, cellTestId: string) => {
      switch (key) {
        case 'service':
          return (
            <div className={`${PF}__cell-service`}>
              <FontAwesomeIcon icon={faCircle} className="point" />
              <Text data-testid={cellTestId}>{row[key]}</Text>
            </div>
          );
        case 'discount':
          return (
            <div>
              <Text data-testid={cellTestId}>{divideAmount(row[key])}</Text>{' '}
              {row.discounts && row.discounts.length > 0 && (
                <Tooltip
                  placement="top"
                  title={
                    <div>
                      {row.discounts.map((discount, index) => (
                        <div key={index}>
                          <Text>{getDiscountTooltipText(discount)}</Text>
                        </div>
                      ))}
                    </div>
                  }
                >
                  <InfoCircleOutlined />
                </Tooltip>
              )}
            </div>
          );
        default:
          const value = row[key];
          return (
            <Text data-testid={cellTestId}>
              {divideAmount(value as number)}
            </Text>
          );
      }
    },
    []
  );

  const getTotal = useCallback(
    (key: keyof ServiceTableRow) =>
      divideAmount(
        data
          .reduce((acc, row) => acc + (row[key] as number), 0)
          .toLocaleString('ru-RU')
      ),
    [data]
  );

  return (
    <div className={`${PF}__container`}>
      <table className={`${PF}__table`}>
        <thead>
          <tr>
            {columns.map(({ key, title, className, columnTestId }) => (
              <th
                key={key}
                className={`${PF}__th ${className}`}
                data-testid={columnTestId}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data?.length > 0 ? (
            data.map((row) => (
              <tr key={row.key} className={`${PF}__row`}>
                {columns.map(({ key, cellTestId }) => (
                  <td key={key} className={`${PF}__cell`}>
                    {getCell(key, row, cellTestId)}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={columns.length} className={`${PF}__empty`}>
                <Flex justify="center" align="center" className="full-width">
                  <Empty description="Нет выбранных услуг" />
                </Flex>
              </td>
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr className={`${PF}__footer`}>
            <td className={`${PF}__footer-cell`}>
              <Text strong data-testid="total">
                Итого:
              </Text>
            </td>
            {totals.map(({ key, cellTestId, colSpan, postFix }) => (
              <td key={key} colSpan={colSpan} className={`${PF}__footer-cell`}>
                <Text strong data-testid={cellTestId}>
                  {getTotal(key)} {postFix}
                </Text>
              </td>
            ))}
          </tr>
          {data?.length > 0 && (
            <tr className={`${PF}__footer`}>
              <td colSpan={columns.length} className={`${PF}__footer-cell`}>
                <Button
                  type="primary"
                  className={`${PF}__footer-button`}
                  icon={<FontAwesomeIcon icon={faFilePdf} />}
                  disabled={isPrintPdfLoading}
                  loading={isPrintPdfLoading}
                  onClick={printPdfFile}
                  data-testid="create-invoice"
                >
                  Распечатать детали продажи в PDF
                </Button>
              </td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
};
